import { Box, Card, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { getChartColors } from '../utils/chartConfig';

/**
 * RiskScaleSection - Risk Analysis & Fleet Scaling
 *
 * Shows downside protection, risk register, applicability, and scale projections.
 * Matches Print View Page 8 content.
 */
export default function RiskScaleSection({ roiResults, cfoMetrics }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);
  const summary = roiResults?.portfolio?.summary || {};

  // Scale calculations
  const totalEquipment = summary.count || 1;
  const unitSavings = (summary.total_annual_savings || 0) / totalEquipment;
  const unitCost = (summary.total_treatment_cost || 0) / totalEquipment;

  // Break-even threshold
  const capex = summary.total_treatment_cost || 0;
  const totalAnnualValue = summary.total_annual_value || summary.total_annual_savings || 0;
  const breakEvenThresholdPct = capex > 0 && totalAnnualValue > 0
    ? Math.round((capex / (totalAnnualValue * 5)) * 100)
    : 20;

  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val.toFixed(0)}`;
  };

  const risks = [
    {
      risk: 'Savings variance from projection',
      likelihood: 'Low',
      impact: 'Low',
      mitigation: 'ASHRAE documents 2-3%/year efficiency loss. 113 PE-certified studies confirm treatment recovers this loss.',
    },
    {
      risk: 'Equipment replacement during treatment',
      likelihood: 'Low',
      impact: 'None',
      mitigation: 'Product guarantee provides treatment for replacement system at no additional cost.',
    },
    {
      risk: 'M&V measurement variability',
      likelihood: 'Low',
      impact: 'Low',
      mitigation: 'Chemical process — outcome is physics-based, not variable.',
    },
  ];

  const applicabilityItems = [
    { label: 'New Equipment', note: 'Prevents degradation before it starts' },
    { label: 'Aged Equipment', note: 'Recovers lost capacity (20-25%)' },
    { label: 'Low Runtime', note: 'Economics scale proportionally' },
    { label: 'Replacement <12mo', note: 'Guarantee covers replacement' },
  ];

  const scaleRows = [
    { scale: 'Current', units: totalEquipment, isCurrent: true },
    { scale: '100 units', units: 100 },
    { scale: '1,000 units', units: 1000 },
    { scale: '10,000 units', units: 10000 },
    { scale: '100,000 units', units: 100000 },
  ];

  const getRiskColor = (level) => {
    const isDark = theme.palette.mode === 'dark';
    switch (level.toLowerCase()) {
      case 'low': return { bg: 'rgba(76, 175, 80, 0.15)', color: isDark ? '#4CAF50' : 'text.primary' };
      case 'none': return { bg: 'rgba(79, 195, 247, 0.15)', color: isDark ? '#4FC3F7' : 'text.primary' };
      case 'medium': return { bg: 'rgba(255, 193, 7, 0.15)', color: isDark ? '#FFC107' : 'text.primary' };
      default: return { bg: 'rgba(255, 255, 255, 0.1)', color: isDark ? 'rgba(255,255,255,0.6)' : 'text.primary' };
    }
  };

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      {/* Section Header */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          fontSize: '1.1rem',
          color: 'text.primary',
          mb: 2,
        }}
      >
        Risk & Scale Analysis
      </Typography>

      {/* Downside Protection Callout */}
      <Card
        sx={{
          p: 2,
          mb: 3,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(76, 175, 80, 0.05) 100%)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <VerifiedUserIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.secondary', fontSize: '1.3rem', mt: 0.25 }} />
        <Box>
          <Typography
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              fontSize: '0.85rem',
              color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
              mb: 0.5,
            }}
          >
            Downside Protection
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)', lineHeight: 1.5 }}>
            Investment remains NPV-positive even if only <strong>{breakEvenThresholdPct}%</strong> of documented savings materialize.
          </Typography>
        </Box>
      </Card>

      {/* Risk Register */}
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'text.secondary',
          mb: 1.5,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Risk Register
      </Typography>
      <TableContainer
        component={Card}
        sx={{
          mb: 3,
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { borderColor: (theme) => theme.palette.divider } }}>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Risk</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Likelihood</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Impact</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Mitigation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {risks.map((row, idx) => (
              <TableRow key={idx} sx={{ '& td': { borderColor: (theme) => theme.palette.divider } }}>
                <TableCell sx={{ color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)', fontSize: '0.7rem', py: 1.5 }}>{row.risk}</TableCell>
                <TableCell>
                  <Chip
                    label={row.likelihood}
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      height: 20,
                      fontWeight: 600,
                      backgroundColor: getRiskColor(row.likelihood).bg,
                      color: getRiskColor(row.likelihood).color,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.impact}
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      height: 20,
                      fontWeight: 600,
                      backgroundColor: getRiskColor(row.impact).bg,
                      color: getRiskColor(row.impact).color,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.4, maxWidth: 300 }}>
                  {row.mitigation}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Universal Applicability */}
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'text.secondary',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Universal Applicability
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.5 }}>
        Unlike VFDs or lighting retrofits, PermaFrost NMR treatment applies to all operational HVAC equipment:
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {applicabilityItems.map((item, idx) => (
          <Grid item xs={6} md={3} key={idx}>
            <Card
              sx={{
                p: 1.5,
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.03) 100%)',
                border: '1px solid rgba(76, 175, 80, 0.15)',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                <CheckCircleIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary', fontSize: '0.7rem' }} />
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary' }}>Treat</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                {item.note}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', fontStyle: 'italic', mb: 3 }}>
        <strong>Only exclusion:</strong> Equipment scheduled for decommissioning within 6 months.
      </Typography>

      {/* Fleet Scale Projections */}
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'text.secondary',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Fleet Scale Projections
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.5 }}>
        Per-unit economics validated for repeatable deployment across your installed base.
      </Typography>
      <TableContainer
        component={Card}
        sx={{
          mb: 3,
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { borderColor: (theme) => theme.palette.divider } }}>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Fleet Scale</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Annual Savings</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>Treatment Cost</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>5-Year Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scaleRows.map((row) => (
              <TableRow
                key={row.scale}
                sx={{
                  '& td': { borderColor: (theme) => theme.palette.divider },
                  backgroundColor: row.isCurrent ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                  ...(row.isCurrent && { border: '1px solid rgba(76, 175, 80, 0.3)' }),
                }}
              >
                <TableCell sx={{ color: 'text.primary', fontSize: '0.7rem', py: 1.25, fontWeight: row.isCurrent ? 600 : 400 }}>
                  {row.scale}
                  {row.isCurrent && (
                    <Chip
                      label="This analysis"
                      size="small"
                      sx={{
                        ml: 1,
                        fontSize: '0.55rem',
                        height: 16,
                        fontWeight: 600,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(0, 0, 0, 0.08)',
                        color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                      }}
                    />
                  )}
                </TableCell>
                <TableCell sx={{ color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)', fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace' }}>
                  {formatCurrency(unitSavings * row.units)}/yr
                </TableCell>
                <TableCell sx={{ color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)', fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace' }}>
                  {formatCurrency(unitCost * row.units)}
                </TableCell>
                <TableCell sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary', fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}>
                  {formatCurrency(unitSavings * row.units * 5)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Deployment Roadmap */}
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: 'text.secondary',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Deployment Roadmap
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.5 }}>
        Start small, scale fast as results prove out:
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.06)' : '#fff',
              border: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(76, 175, 80, 0.3)' : '2px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 1.5,
            }}
          >
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              Phase 1: Proof of Concept
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
              5% or greater of portfolio
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic' }}>
              Gate: Verified savings confirm projections
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 2,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.06)' : '#fff',
              border: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(255, 193, 7, 0.3)' : '2px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 1.5,
            }}
          >
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
              Phase 2: Rapid Scale
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
              Remaining portfolio
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic' }}>
              Gate: Continue rollout as results prove out
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
